import { RawOrganisationData } from '@frontend/core'
import {
  handleOrganisationSwitch,
  useOrganisations_getAllQuery,
  useOrganisations_getByIdQuery
} from '@frontend/core/lib/domains/organisations'
import { useAppDispatch, useAppSelector } from '@frontend/react'
import { CustomFilterState } from '@frontend/utils'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useEffect, useMemo, useState } from 'react'
import { OrganisationFilters, useOrganisationsFilter } from './filter'

export const useOrganisationsData = (
  customFilterState: CustomFilterState<OrganisationFilters>,
  defaultSelectedId: string | null = null
) => {
  const query = useOrganisations_getAllQuery(undefined, {})

  const list = useMemo(() => {
    if (!query.data?.entities) return []
    return Object.values(query.data.entities)
  }, [query.data?.entities])

  // Client side filtering //
  // Gives you filter state, filteredData and functions to update filter values / options //
  const filter = useOrganisationsFilter(list, customFilterState)

  const map: Record<string, RawOrganisationData> = useMemo(() => {
    return query.data?.entities || {}
  }, [query.data?.entities])

  const options = useMemo(() => {
    return filter.filteredData.map((organisation: RawOrganisationData) => ({
      name: organisation.name,
      value: organisation.id,
      id: organisation.id,
      sport: organisation.sport,
      teamCount: organisation.teamCount,
      adminCount: organisation.adminCount,
      userCount: organisation.userCount
    }))
  }, [filter.filteredData])

  const [selectedId, setSelectedId] = useState(defaultSelectedId || null)

  const selectedItem: RawOrganisationData | null = useMemo(() => {
    if (!selectedId) return null
    return map[selectedId] || null
  }, [selectedId, map])

  return {
    ...query,
    ...filter,
    map,
    options,
    selectedId,
    setSelectedId,
    selectedItem
  }
}

export const useOrganisation = (organisationId: string): OrganisationData => {
  const { data, isLoading, isFetching, isError } =
    useOrganisations_getByIdQuery(organisationId, {})
  return { organisation: data ?? null, isLoading, isFetching, isError }
}

export const useSelectedOrganisationId = () => {
  return useAppSelector((state) => state.organisations.selectedId)
}

export interface OrganisationData {
  organisation: RawOrganisationData | null
  isLoading: boolean
  isFetching: boolean
  isError: boolean
}

// With no remembered id the query is skipped rather than sent with an empty
// id: isLoading stays false and data undefined, so the fallback below treats
// it as settled-with-nothing and can fire as soon as the list arrives.
export const useSelectedOrganisation = (): OrganisationData => {
  const selectedId = useSelectedOrganisationId()
  const { data, isLoading, isFetching, isError } =
    useOrganisations_getByIdQuery(selectedId ?? skipToken)

  return { organisation: data ?? null, isLoading, isFetching, isError }
}

// While the selected-organisation query is loading, a missing organisation only
// means "not confirmed yet" — switching then would discard the remembered
// selection, so the fallback waits for the query to settle.
export const useOrganisationFallback = (
  organisations: RawOrganisationData[]
) => {
  const dispatch = useAppDispatch()
  const { organisation, isLoading } = useSelectedOrganisation()

  useEffect(() => {
    if (!isLoading && !organisation && organisations.length > 0) {
      dispatch(handleOrganisationSwitch(organisations[0].id))
    }
  }, [isLoading, organisation, organisations, dispatch])
}
